/**
 * The console's binding of the shared navigation column (#1991, ruling 72563).
 *
 * `packages/ui/src/shell/NavigationShell.tsx` owns the SHAPE — identity above, a
 * scrolling middle, the signed-in user below, and the governed mode switch
 * between the first two. This file owns what the console has to say in each
 * region, which is the half that may know about a brand setting, an environment
 * door and a router.
 *
 * ── it wraps BOTH modes, and that is the ruling ────────────────────────────
 * Ruling 72563: *"The editor must preserve this read-mode composition rather
 * than replace it with an unrelated administration screen."* So `AppShell`
 * renders this around whichever middle it is drawing — `<Nav/>` or
 * `<NavEditMode/>` — instead of around the reading one only. The identity and
 * the user footer are not the editor's to redraw, so they cannot drift from the
 * bar a moment earlier, and the account trigger that focus returns to on exit
 * stays mounted for the whole of edit mode rather than being remounted under it.
 *
 * ── WHAT MOVED HERE FROM THE TOP BAR, AND WHY EACH ─────────────────────────
 * Ruling §1 puts *"Moonbase mark, product/host name and environment/status"* in
 * one deliberate header. Measured at `b9ec7338` the console had all three, in
 * `Topbar` — a full-width row ABOVE the shell grid — and the nav column opened
 * directly on its first group with no header and no footer at all.
 *
 *   · **environment/status** moves. It is a statement about the deployment you
 *     are navigating, it is read by no spec anywhere in the tree (checked), and
 *     leaving it on the top bar would have made §1 a header assembled from two
 *     regions.
 *   · **the brand lockup moves with the navigation pane.** The fixed rail and
 *     compact drawer render the same identity, while the workspace header owns
 *     no duplicate logo and starts exactly at the pane boundary (#2328/#2358).
 *   · **the account menu STAYS on the top bar** — see the §7 section below,
 *     which measures why. What moves here is the identity beside it.
 *
 * ── §7 IS SUPERSEDED FOR THE USER FOOTER (#2328 §1, ROOT ruling 74071 §3) ──
 * Ruling 72563 §7 put the signed-in user at the BOTTOM of this column, and the
 * three measurements below are what it took to make that panel usable. #2328 §1
 * supersedes the placement — not the measurements, which is why they are kept
 * verbatim: *"Right-aligned global controls, in governed order: global search;
 * Recent & Pinned; signed-in user / active persona"*, and *"the header remains
 * stable across Console pages"*. A control that lives in the navigation column
 * on a desktop and in the header on a phone is not stable, and with the persona
 * panel added it is also the largest surface in the shell — a 320px panel
 * escaping a 220px clipped column was already three defects deep.
 *
 * So this column passes NO `user`, and `NavigationShell` renders no footer
 * region at all (its own contract: *"a host with no user footer passes no
 * `user` and gets no empty bar with a blank circle in it"*). What remains here
 * is the identity HEADER, which is a statement about the deployment and is
 * unaffected. `placement="above"` survives in `UserMenu` because the portal
 * still mounts a footer-anchored menu — this file simply stops being its second
 * caller.

 * ── §7, IN FULL, AND WHAT IT TOOK ─────────────────────────────────────────
 * §7 asks the footer for "avatar/initials, name, role and account/settings
 * access". Identity and access are both here. Getting there took three
 * measurements, in this order, and each was a real defect:
 *
 *   1. the panel opened DOWNWARD from the bottom of a fixed column, so Sign
 *      out sat below the viewport with nothing to scroll into;
 *   2. flipped upward it anchored right: 0, which in a 220px column put a
 *      320px panel at x = -105 in a 1440px window;
 *   3. anchored on both axes it was CLIPPED by the column, which must keep
 *      overflow: hidden so its middle stays the only scroller — Sign out at
 *      centre x=221, one pixel past the clip, elementFromPoint returning the
 *      page behind.
 *
 * The third is why the panel is position: fixed rather than portalled.
 * UserMenu own header states that the panel follows its trigger in DOM order
 * so Tab walks into it, and a portal makes that quietly false. Fixed escapes
 * the clip and leaves focus, Escape, outside-click and the tab walk alone.
 *
 * The ROLE is still absent, and still not fabricated — see below.
 *
 * ── the mode switch is not passed, and that is a measurement ───────────────
 * §2: *"where the host has reviewed modes such as Backstage / Workspace, render
 * one compact segmented switch. Mode is governed context; it is not a fabricated
 * client filter and must not appear where the host declares only one mode."*
 *
 * Nothing in this tree declares a host's modes as DATA — not `core.navigation`,
 * not `core.experience`, not `access.my_areas`. `my_areas` answers which areas a
 * person may ENTER, which is an authorization answer and not a mode: deriving a
 * switch from it would be SKILL rule 16 exactly — reading a grant to decide what
 * something IS. So the console declares one mode today and passes no `modes`,
 * the shared component renders nothing, and the day a door carries them this
 * file passes them through with no change to the shape. Asked on #1991.
 *
 * ── the role line is not fabricated either ────────────────────────────────
 * §7 asks the footer for a role. `core.my_profile()` returns display_name,
 * email, idp, name_is_idp_owned and settable — no role — and every door that
 * does answer "which roles does this user hold" (`access.list_users`) is
 * admin-gated, so a non-admin cannot read their own. `my_areas()` is available
 * and is the wrong answer for the reason above. No `role` is passed rather than
 * a guess: the gap is a door (`core.my_profile()` carrying the person's own
 * title), reported with the lane, not a client invention.
 */
import { type ReactNode, type RefObject } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BrandLogo, NavigationShell, useTheme } from '@solum/ui'
import { useSolum } from '../data/client'
import { loadEnvironmentHealth } from '../ops/setupLive'
import s from './ConsoleNavigationShell.module.css'

/** WHICH host this is. Shared code may not know it; this one must. */
const HOST_NAME = 'Console'

export function ConsoleNavigationShell({ children, collapse, userSlot }: {
  /** Kept in the signature so `AppShell` passes one ref to one owner. Unused
   *  here since the menu moved to the header; `Topbar` forwards it now. */
  accountTrigger?: RefObject<HTMLButtonElement | null>
  onPersonalizeNav?: () => void
  /** The rail's collapse state, owned by `AppShell` with the grid column. */
  collapse?: { collapsed: boolean; onToggle: () => void }
  /**
   * Where the ONE account menu draws its second trigger (#2406 leading
   * designs: the navbar sheet caps the column with the user). A ref callback,
   * because the menu lives in the header and portals the cap into this
   * element; absent — the phone drawer — draws no cap, since the drawer is a
   * modal sheet over an inert header and a trigger in it could only open a
   * menu nobody can reach. The header trigger is on the phone bar already.
   */
  userSlot?: (element: HTMLElement | null) => void
  children: ReactNode
}) {
  const { brand } = useTheme()
  const { isLive, client } = useSolum()
  /* The same query key the top bar used, so moving the reading did not add a
     second poll of the same door — react-query dedupes on the key. */
  const environment = useQuery({
    queryKey: ['environment-health'], enabled: isLive,
    queryFn: () => loadEnvironmentHealth(client), refetchInterval: 60_000,
  })

  return (
    <NavigationShell
      /* The inset `.side` used to carry, declared rather than inherited — the
         shared middle pads nothing (see `middleClassName`). */
      middleClassName={s.middle}
      identity={{
        /* ── THE LOCKUP LIVES IN THE NAVIGATION PANE (#2328/#2358) ─────────
           `NavigationShell` owns the same rendered logo or glyph in the fixed
           rail and compact drawer. The workspace header renders no duplicate. */
        /* The HANDLE is on the lockup (`identity.testId`), not on this glyph.
           It carried `brand-lockup` for one CI round and `xss.spec.ts` found the
           consequence: that spec sets a logo whose bytes are not an image, the
           glyph collapsed to 0x0, and the hydration witness ten specs wait on
           reported the shell as never having appeared. The lockup always carries
           the deployment name, so it always has a box. */
        mark: brand.logo
          ? <BrandLogo logo={brand.logo} testId="brand-logo" />
          : brand.mark,
        testId: 'brand-lockup',
        name: brand.name,
        host: HOST_NAME,
        status: isLive && environment.data
          ? (
              <span className={s.environment} data-testid="environment-health"
                    title={environment.data.backup.detail}>
                <span>{environment.data.identity.instance_type.toUpperCase()}</span>
                <i data-state={environment.data.backup.status} />
                <span className={s.healthLabel}>
                  {environment.data.backup.status === 'ok' ? 'Recovery ready' : 'Recovery attention'}
                </span>
              </span>
            )
          : undefined,
      }}
      collapse={collapse}
      user={userSlot ? <div ref={userSlot} className={s.userSlot} data-testid="rail-user-slot" /> : undefined}
      /* THE USER CAP IS BACK, AS A SLOT (#2406 leading designs). #2328 §1 made
         the header the one home of the account and this column passed no
         `user`; the owner's leading designs keep that header trigger AND cap
         the column with the user (the navbar sheet, marked LOCKED). The leading
         design wins, and #2328 §1's reason still holds for what it was about:
         there is ONE account menu. What sits here is an empty element the
         header's menu draws its second trigger into — not a name and a disc
         in a region nobody can act in. */
    >
      {children}
    </NavigationShell>
  )
}
